import type { CompanyRegistration } from "@finn-no/mobility-dealer-types";
import pulse from "@finn-no/pulse-sdk";
import { getCategoryForSearchKey } from "@m10s/mobility-mapping-utils";
// @ts-expect-error - The type is exported
import type { SearchKey } from "@m10s/search-frontend-clients/quest";
import { ElementType, EventType, Intent, TargetType } from "@mobility-item-page/utilities/client";

export const trackPageView = (pageTitle: string, orgId: number) => {
  pulse.trackPageView({
    type: EventType.View,
    object: {
      id: "DBP_pv01",
      type: "Page",
      name: pageTitle,
      pageType: "dealer_branding_page",
      contentId: orgId.toString(),
    },
    vertical: {
      name: "mobility",
    },
  });
};

export const trackSeeLocation = (elementType: ElementType, linkUrl?: string) => {
  if (linkUrl) {
    const { hostname } = new URL(linkUrl);
    pulse.trackEvent({
      type: EventType.Click,
      intent: Intent.Open,
      name: "Dealer map",
      object: {
        id: "DBP_ev08",
        type: "UIElement",
        elementType,
      },
      target: {
        id: linkUrl,
        type: hostname === "google.com" || hostname.endsWith(".google.com") ? "ExternalContent" : "Page",
      },
      vertical: {
        name: "mobility",
      },
    });
  }
};

export const trackSeeOtherLocation = (linkUrl?: string) =>
  linkUrl &&
  pulse.trackEvent({
    type: EventType.Click,
    intent: Intent.Open,
    name: "Other dealer map",
    object: {
      id: "DBP_ev13",
      type: "UIElement",
      elementType: ElementType.Link,
    },
    target: {
      id: linkUrl,
      type: "Page",
    },
    vertical: {
      name: "mobility",
    },
  });

export const trackBrandingPage = (linkUrl: string, providerName?: string) =>
  pulse.trackEvent({
    type: EventType.Click,
    intent: Intent.View,
    name: "Dealer branding page",
    object: { id: "DBP_ev11", type: "UIElement", elementType: ElementType.Link },
    target: {
      id: linkUrl,
      type: TargetType.Page,
      provider: providerName,
    },
    vertical: {
      name: "mobility",
    },
  });

export const trackShowFullDescription = () =>
  pulse.trackEvent({
    type: EventType.Click,
    intent: Intent.View,
    name: "Full dealer description",
    object: {
      id: "DBP_ev05",
      type: "UIElement",
      elementType: ElementType.Button,
      hasExtendedProfile: false,
    },
    vertical: {
      name: "mobility",
    },
  });

export const trackHomepage = (orgName: string, url?: string) => {
  pulse.trackEvent({
    type: EventType.Click,
    intent: Intent.Open,
    name: "Homepage link clicked",
    object: { id: "DBP_ev06", type: "UIElement", elementType: ElementType.Link },
    target: {
      id: url ?? "",
      type: TargetType.ExternalContent,
      name: "Go to webpage",
      provider: orgName,
    },
    vertical: {
      name: "mobility",
    },
  });
};

export const trackCompanyRegistration = (companyRegistration: CompanyRegistration) => {
  pulse.trackEvent({
    type: EventType.Click,
    intent: Intent.Open,
    name: "Dealer registration number lookup",
    object: { id: "DBP_ev02", type: "UIElement", elementType: ElementType.Link },
    target: {
      id: companyRegistration.registrationNumberLinkUrl,
      type: TargetType.ExternalContent,
      name: "Go to webpage",
      provider: companyRegistration.registrationProviderName,
    },
    vertical: {
      name: "mobility",
    },
  });
};

export const trackDealerRatings = (url: string) => {
  pulse.trackEvent({
    type: EventType.Click,
    intent: Intent.Open,
    name: "External dealer ratings",
    object: { id: "DBP_ev15", type: "UIElement", elementType: ElementType.Link },
    target: {
      id: url,
      type: TargetType.ExternalContent,
      name: "Go to webpage",
    },
    vertical: {
      name: "mobility",
    },
  });
};

export const trackShowPhone = (orgName: string) => {
  pulse.trackEvent({
    type: EventType.Click,
    intent: Intent.Show,
    name: "Contact seller",
    object: {
      id: "DBP_ev03",
      type: "UIElement",
      elementType: "Phone number",
    },
    target: {
      id: "show-phone-numbers",
      type: TargetType.ExternalContent,
      name: "Phone",
      provider: orgName,
    },
    vertical: {
      name: "mobility",
    },
  });
};

export const trackPhone = (intent: Intent.Call | Intent.Click, phoneNumber: string, orgName: string) => {
  pulse.trackEvent({
    type: EventType.Click,
    intent,
    name: "Contact seller",
    object: {
      id: "DBP_ev04",
      type: "UIElement",
      elementType: "Phone number",
    },
    target: {
      id: phoneNumber,
      type: TargetType.ExternalContent,
      name: "Phone",
      provider: orgName,
    },
    vertical: {
      name: "mobility",
    },
  });
};

export const trackNavigationClick = (tab: string, linkUrl: string) => {
  pulse.trackEvent({
    type: EventType.Click,
    intent: Intent.View,
    name: "Selected page section",
    object: {
      id: "DBP_ev01",
      type: "UIElement",
      name: tab,
      elementType: ElementType.Link,
    },
    target: {
      id: linkUrl,
      type: "UIElement",
      name: `${tab} section`,
    },
    vertical: {
      name: "mobility",
    },
  });
};

export const trackCarouselScroll = (direction: "backwards" | "forwards", searchKey: SearchKey) => {
  pulse.trackEvent({
    type: EventType.Click,
    intent: Intent.View,
    name: "Scroll more ads from us carousel",
    object: {
      id: "DBP_ev09",
      type: "UIElement",
      position: direction,
      elementType: ElementType.Button,
      category: getCategoryForSearchKey(searchKey, <URLSearchParams>{}),
    },
    target: {
      id: `${direction === "backwards" ? "previous" : "next"}-ads-in-carousel`,
      type: "UIElement",
    },
    vertical: {
      name: "mobility",
    },
  });
};

export const trackSeeAllListings = (searchKey: string, link: string, orgName: string) => {
  pulse.trackEvent({
    type: EventType.Click,
    intent: Intent.View,
    name: "See all listings",
    object: {
      id: "DBP_ev10",
      type: "UIElement",
      elementType: ElementType.Button,
      category: getCategoryForSearchKey(searchKey, <URLSearchParams>{}),
    },
    target: {
      id: link,
      type: TargetType.Listing,
      provider: orgName,
    },
    vertical: {
      name: "mobility",
    },
  });
};

export const trackSeeAdInCategory = (adId: number, adTitle?: string, searchKey?: string, linkUrl?: string) => {
  linkUrl &&
    pulse.trackEvent({
      type: EventType.Click,
      intent: Intent.Open,
      name: "Dealer ad",
      object: {
        id: "DBP_ev12",
        type: "ListingItem",
        name: adTitle,
      },
      target: {
        id: linkUrl,
        type: TargetType.ClassifiedAd,
        name: adTitle || "",
        contentId: adId.toString(),
        category: getCategoryForSearchKey(searchKey, <URLSearchParams>{}),
      },
      vertical: {
        name: "mobility",
      },
    });
};

export const trackViewMap = () =>
  pulse.trackEvent({
    type: EventType.Click,
    intent: Intent.Open,
    name: "View map",
    object: {
      id: "DBP_ev16",
      type: "UIElement",
      elementType: ElementType.Button,
      hasExtendedProfile: false,
    },
    vertical: {
      name: "mobility",
    },
  });

export const trackOpenCookieSettings = () =>
  pulse.trackEvent({
    type: EventType.Click,
    intent: Intent.Edit,
    name: "Cookie settings",
    object: {
      id: "DBP_ev17",
      type: "UIElement",
      elementType: ElementType.Button,
      hasExtendedProfile: false,
    },
    vertical: {
      name: "mobility",
    },
  });

export const trackClickMemberAssociationLink = ({
  targetTitle,
  linkUrl,
  targetType,
}: {
  targetTitle: string;
  linkUrl: string;
  targetType: TargetType.Page | TargetType.ExternalContent;
}) => {
  pulse.trackEvent({
    type: EventType.Click,
    intent: Intent.Open,
    name: "Read about member association",
    objectData: {
      id: "DBP_ev18",
      type: "UIElement",
      elementType: ElementType.Link,
    },
    target: {
      id: linkUrl,
      type: targetType,
      name: targetTitle,
    },
    vertical: {
      name: "mobility",
    },
  });
};

export const trackOpenGallery = () =>
  pulse.trackEvent({
    type: EventType.Click,
    intent: Intent.Open,
    name: "Dealer Image Gallery",
    object: {
      id: "DBP_ev19",
      type: "UIElement",
      elementType: ElementType.Image,
      hasExtendedProfile: false,
    },
    vertical: {
      name: "mobility",
    },
  });
